import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { normalizeRoute } from "./breadcrumbStackUtils";

const BreadcrumbStackContext = createContext(null);

const sameEntry = (a, b) =>
  a && b && a.id === b.id && normalizeRoute(a.route) === normalizeRoute(b.route);

/** @typedef {{ id: string, labelKey: string, route: string, state?: object, hash?: string, guardProfile?: string, internalBack?: boolean }} BreadcrumbEntry */

export function BreadcrumbStackProvider({ children }) {
  const [entries, setEntries] = useState([]);
  const location = useLocation();
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const reset = useCallback(() => setEntries([]), []);

  const push = useCallback((entry) => {
    if (!entry?.id || !entry?.route) return;
    const normalized = { ...entry, route: normalizeRoute(entry.route) };
    setEntries((prev) => {
      const top = prev[prev.length - 1];
      if (sameEntry(top, normalized)) {
        return [...prev.slice(0, -1), normalized];
      }
      return [...prev, normalized];
    });
  }, []);

  const replaceTop = useCallback((entry) => {
    if (!entry?.id || !entry?.route) return;
    const normalized = { ...entry, route: normalizeRoute(entry.route) };
    setEntries((prev) => {
      if (prev.length === 0) return [normalized];
      return [...prev.slice(0, -1), normalized];
    });
  }, []);

  const ensureTrail = useCallback((ancestors = [], entry, { replaceSameRoute = false } = {}) => {
    if (!entry?.id || !entry?.route) return;
    const normalized = { ...entry, route: normalizeRoute(entry.route) };
    setEntries((prev) => {
      let next = [...prev];
      for (const ancestor of ancestors) {
        if (!ancestor?.id) continue;
        const a = { ...ancestor, route: normalizeRoute(ancestor.route) };
        if (!next.some((e) => e.id === a.id)) {
          next.push(a);
        }
      }
      const existingIdx = next.findIndex((e) => e.id === normalized.id);
      if (existingIdx >= 0) {
        next = next.slice(0, existingIdx);
      }
      const top = next[next.length - 1];
      if (
        replaceSameRoute &&
        top &&
        normalizeRoute(top.route) === normalizeRoute(normalized.route) &&
        top.id !== normalized.id
      ) {
        next = [...next.slice(0, -1), normalized];
      } else {
        next.push(normalized);
      }
      return next;
    });
  }, []);

  const popToIndex = useCallback((index) => {
    setEntries((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      return prev.slice(0, index + 1);
    });
  }, []);

  // Safe teardown for a conditionally-registered entry (useRegisterBreadcrumbEntry's
  // cleanup): only pops when it's still the top — if something else pushed on
  // top in the meantime, leave it for whatever superseded it to manage.
  // `restoreEntry` (optional) is pushed in the same update when the pop does
  // happen — for an entry that itself replaced a sibling on mount (e.g.
  // RewampPreviewFormPage's "scheme-preview" swapping out SchemePageForm's
  // "scheme-apply"), so stepping back restores what was displaced instead of
  // leaving the persistent parent's entry gone for good.
  const popIfTop = useCallback((id, restoreEntry) => {
    setEntries((prev) => {
      if (prev.length === 0) return prev;
      if (prev[prev.length - 1].id !== id) return prev;
      const next = prev.slice(0, -1);
      if (!restoreEntry) return next;
      return [...next, { ...restoreEntry, route: normalizeRoute(restoreEntry.route) }];
    });
  }, []);

  // Erase a named run of entries (e.g. "scheme-apply-form"): removes the
  // group's first member and everything after it, leaving whatever preceded
  // the group on top.
  const popGroup = useCallback((groupId) => {
    setEntries((prev) => {
      const idx = prev.findIndex((e) => e.group === groupId);
      if (idx === -1) return prev;
      return prev.slice(0, idx);
    });
  }, []);

  const getParent = useCallback(() => {
    const prev = entriesRef.current;
    return prev.length >= 2 ? prev[prev.length - 2] : null;
  }, []);

  const getEntryAt = useCallback((index) => entriesRef.current[index] ?? null, []);

  // Trim stack when location moves to an earlier crumb (browser back / guarded leave).
  // findLastIndex — nested steps can share a route (login › non-DBT registration
  // are both /login); matching the first entry would drop the active step.
  useEffect(() => {
    const current = normalizeRoute(location.pathname);
    setEntries((prev) => {
      if (prev.length === 0) return prev;
      const idx = prev.findLastIndex((e) => normalizeRoute(e.route) === current);
      if (idx === -1 || idx === prev.length - 1) return prev;
      return prev.slice(0, idx + 1);
    });
  }, [location.pathname]);

  const value = useMemo(
    () => ({
      entries,
      hasStack: entries.length > 0,
      push,
      replaceTop,
      ensureTrail,
      popToIndex,
      popIfTop,
      popGroup,
      reset,
      getParent,
      getEntryAt,
    }),
    [entries, push, replaceTop, ensureTrail, popToIndex, popIfTop, popGroup, reset, getParent, getEntryAt]
  );

  return (
    <BreadcrumbStackContext.Provider value={value}>
      {children}
    </BreadcrumbStackContext.Provider>
  );
}

export function useBreadcrumbStack() {
  const ctx = useContext(BreadcrumbStackContext);
  if (!ctx) {
    throw new Error("useBreadcrumbStack must be used within BreadcrumbStackProvider");
  }
  return ctx;
}

export function useBreadcrumbStackOptional() {
  return useContext(BreadcrumbStackContext);
}

/**
 * Register a page on the breadcrumb stack for the duration of `enabled`.
 * `ancestors` seeds Home › … prefix when the user deep-links in.
 * `restoreOnUnmount` (optional entry) is pushed back if, on teardown, this
 * entry is still top of stack — for a registration that itself replaced a
 * sibling on mount (`replace`/`ensureTrail`'s replaceSameRoute), so a
 * persistent parent's displaced entry comes back when this one goes away
 * instead of staying gone. Not memoized by callers — read via a ref so a
 * fresh object every render doesn't re-fire the effect.
 */
export function useRegisterBreadcrumbEntry(
  entry,
  { enabled = true, ancestors, replace = false, restoreOnUnmount } = {},
) {
  const stack = useBreadcrumbStackOptional();
  const ancestorKey = ancestors?.map((a) => a.id).join("|") ?? "";
  const entryKey = entry
    ? `${entry.id}|${normalizeRoute(entry.route)}|${entry.labelKey ?? ""}`
    : "";
  const restoreRef = useRef(restoreOnUnmount);
  restoreRef.current = restoreOnUnmount;
  // Depend on the individual methods (stable — each is useCallback([]) in
  // BreadcrumbStackProvider), never on `stack` itself. The Provider's
  // `value` object is re-memoized on every `entries` change, so depending
  // on `stack` re-runs this effect after every push/pop ANYWHERE in the
  // app — including the mutation this same effect just performed — which
  // is a genuine infinite render loop (confirmed via a real mount in
  // BreadcrumbStackContext.test.js: "Maximum update depth exceeded").
  const push = stack?.push;
  const replaceTop = stack?.replaceTop;
  const ensureTrail = stack?.ensureTrail;
  const popIfTop = stack?.popIfTop;

  useEffect(() => {
    if (!push || !enabled || !entry) return undefined;
    if (ancestors?.length) {
      ensureTrail(ancestors, entry, { replaceSameRoute: replace });
    } else if (replace) {
      replaceTop(entry);
    } else {
      push(entry);
    }
    // Only pop this entry back out if it's still the top when `enabled`
    // flips false / the component unmounts — a later registration
    // (replaceTop/ensureTrail) that superseded it already owns cleanup.
    return () => popIfTop(entry.id, restoreRef.current);
  }, [push, replaceTop, ensureTrail, popIfTop, enabled, replace, ancestorKey, entryKey, entry]);
}

/** Navigate to parent stack entry via guardedNavigate; falls back to onFallback. */
export function useBreadcrumbStackBack({ guardedNavigate, onFallback, onInternalBack }) {
  // `getParent` specifically (stable — useCallback([])), not `stack` itself,
  // which changes identity on every `entries` update — see the same note in
  // useRegisterBreadcrumbEntry. Not currently in any effect's deps, so this
  // wasn't causing a loop, but keeping the same defensive convention here
  // avoids a footgun if this callback's identity is ever depended on.
  const getParent = useBreadcrumbStackOptional()?.getParent;

  return useCallback(() => {
    if (typeof onInternalBack === "function") {
      onInternalBack();
      return;
    }
    const parent = getParent?.();
    if (parent && guardedNavigate) {
      guardedNavigate(parent.route, {
        state: parent.state,
        hash: parent.hash,
      });
      return;
    }
    onFallback?.();
  }, [getParent, guardedNavigate, onFallback, onInternalBack]);
}

export default BreadcrumbStackContext;
